/* ==========================================================================
   PACK BUILDER  --  behaviour layer
   --------------------------------------------------------------------------
   - Reads ?board=<handle> from URL and swaps SSR DOM to match if needed.
   - Tracks accessory selection per category (housse | leash | ailerons | wax).
   - Computes UI-only subtotal / discount estimate / total.
   - Adds the pack to cart via POST {localeRoot}cart/add.js with a shared _pack_id property.

   Real discount is NOT applied here. It must be configured via a Shopify
   Automatic Discount rule that matches line items carrying a _pack_id property.
   ========================================================================== */

(function () {
  'use strict';

  const root = document.querySelector('.pack-builder');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const dataEl = document.getElementById('packBuilderData-' + sectionId);
  if (!dataEl) return;

  let DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.error('[pack-builder] invalid data blob', err);
    return;
  }

  /** @returns {string} Root path for Cart/Ajax API (e.g. `/` or `/fr/`) — required on multilingual shops. */
  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  const CATEGORIES = ['housse', 'leash', 'ailerons', 'pad', 'wax'];
  const MF_KEYS = {
    housse: 'pack_housses',
    leash: 'pack_leashes',
    ailerons: 'pack_ailerons',
    pad: 'pack_pads',
    wax: 'pack_waxes'
  };

  const state = {
    boardHandle: root.dataset.defaultBoard || '',
    selection: { housse: null, leash: null, ailerons: null, pad: null, wax: null }
  };

  // ------------------------------------------------------------------
  // URL handling
  // ------------------------------------------------------------------
  function getUrlBoard() {
    const params = new URLSearchParams(window.location.search);
    return (params.get('board') || '').trim();
  }

  // ------------------------------------------------------------------
  // Money formatting  --  basic replacement for {{ price | money }}
  // Supports {{amount}} / {{amount_no_decimals}} / {{amount_with_comma_separator}}
  // ------------------------------------------------------------------
  function formatMoney(cents) {
    const format = DATA.money_format || '${{amount}}';
    const n = Number(cents);
    if (Number.isNaN(n)) return format;
    const amount = (n / 100).toFixed(2);
    const amountNoDec = Math.round(n / 100).toString();
    const amountComma = (n / 100).toFixed(2).replace('.', ',');
    const amountCommaNoDec = Math.round(n / 100).toString();
    return format
      .replace('{{amount_no_decimals_with_comma_separator}}', amountCommaNoDec)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount_with_comma_separator}}', amountComma)
      .replace('{{amount}}', amount);
  }

  // ------------------------------------------------------------------
  // DOM helpers
  // ------------------------------------------------------------------
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  function buildCard(item, category, badgeLabel, isSelected) {
    if (!item || item.available === false || !item.variant_id) return null;
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'accessory-card' + (isSelected ? ' selected' : '');
    card.dataset.category = category;
    card.dataset.variantId = item.variant_id;
    card.dataset.price = item.price_cents;
    card.dataset.handle = item.handle;
    card.dataset.name = item.title;
    card.dataset.isPremium = item.is_premium ? 'true' : 'false';
    card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

    const check = document.createElement('span');
    check.className = 'accessory-card__check';
    check.setAttribute('aria-hidden', 'true');
    card.appendChild(check);

    const imgWrap = document.createElement('div');
    imgWrap.className = 'accessory-card__image';
    if (item.image_url) {
      const img = document.createElement('img');
      img.src = item.image_url;
      img.alt = item.title;
      img.loading = 'lazy';
      imgWrap.appendChild(img);
    }
    card.appendChild(imgWrap);

    if (badgeLabel) {
      const badge = document.createElement('span');
      badge.className = 'accessory-card__badge';
      badge.textContent = badgeLabel;
      card.appendChild(badge);
    }

    const name = document.createElement('span');
    name.className = 'accessory-card__name';
    name.textContent = item.title;
    card.appendChild(name);

    if (item.desc) {
      const desc = document.createElement('span');
      desc.className = 'accessory-card__desc';
      desc.textContent = item.desc;
      card.appendChild(desc);
    }

    if (category === 'pad' && Array.isArray(item.variants) && item.variants.length > 1) {
      const group = document.createElement('div');
      group.className = 'pad-variant-swatches';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Couleur');
      item.variants.filter(function (v) { return v && v.available !== false; }).forEach(function (v, idx) {
        const key = (v.title || '').toLowerCase();
        let color = '#cccccc';
        if (key.indexOf('black') !== -1) color = '#000000';
        else if (key.indexOf('dark blue') !== -1 || key.indexOf('marine') !== -1) color = '#0f3057';
        else if (key.indexOf('orange') !== -1) color = '#ff8c42';
        else if (key.indexOf('pink') !== -1) color = '#ff6fa3';
        else if (key.indexOf('turquoise') !== -1 || key.indexOf('lagoon') !== -1) color = '#4ecdc4';
        else if (key.indexOf('yellow') !== -1) color = '#ffd93d';
        else if (key.indexOf('purple') !== -1) color = '#8b5cf6';
        const sw = document.createElement('span');
        sw.setAttribute('role', 'button');
        sw.setAttribute('tabindex', '0');
        const active = String(v.id) === String(item.variant_id);
        sw.className = 'pad-variant-swatch' + (active ? ' is-active' : '');
        sw.dataset.variantId = v.id;
        sw.dataset.price = v.price_cents;
        sw.dataset.title = v.title;
        if (v.image_url) sw.dataset.image = v.image_url;
        sw.setAttribute('aria-pressed', active ? 'true' : 'false');
        sw.style.backgroundColor = color;
        sw.title = v.title;
        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = v.title;
        sw.appendChild(hidden);
        group.appendChild(sw);
      });
      card.appendChild(group);
    }

    const price = document.createElement('span');
    price.className = 'accessory-card__price';
    price.textContent = item.price_formatted || formatMoney(item.price_cents);
    card.appendChild(price);

    return card;
  }

  // ------------------------------------------------------------------
  // Board size variants (surfboard length) — sidebar chips, pattern similar to pad swatches
  // ------------------------------------------------------------------
  function mountBoardVariantPicker(board) {
    const slot = document.getElementById('packBoardVariantSlot');
    if (!slot) return;
    clearChildren(slot);
    const vars = board.variants;
    if (!vars || vars.length < 2) {
      slot.hidden = true;
      return;
    }
    slot.hidden = false;
    const lbl = document.createElement('span');
    lbl.className = 'pack-board-variant__label';
    lbl.textContent = (DATA.i18n && DATA.i18n.board_size_label) || '';

    const group = document.createElement('div');
    group.className = 'board-variant-chips';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', lbl.textContent || 'Size');

    const activeId = board.variant_id;
    vars.forEach(function (v) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'board-variant-chip' + (v.id === activeId ? ' is-active' : '');
      btn.dataset.variantId = String(v.id);
      btn.dataset.priceCents = String(v.price_cents);
      btn.dataset.imageUrl = v.image_url || '';
      btn.textContent = v.label || v.title;
      btn.disabled = !v.available;
      btn.setAttribute('aria-pressed', v.id === activeId ? 'true' : 'false');
      group.appendChild(btn);
    });

    slot.appendChild(lbl);
    slot.appendChild(group);
  }

  // ------------------------------------------------------------------
  // Board swap  --  rebuilds sidebar + grids from JSON when URL board
  // differs from the SSR default (or when SSR had no default).
  // ------------------------------------------------------------------
  function swapToBoard(handle) {
    const board = DATA.boards[handle];
    if (!board) {
      console.warn('[pack-builder] unknown board handle', handle);
      return false;
    }

    state.boardHandle = handle;

    const sb = qs('#packSidebar');
    if (sb) {
      sb.dataset.boardPrice = board.price_cents;
      sb.dataset.boardVariantId = board.variant_id;
    }

    const imgEl = qs('.pack-selected-board__image');
    if (imgEl) {
      clearChildren(imgEl);
      if (board.image_url) {
        const img = document.createElement('img');
        img.src = board.image_url;
        img.alt = board.title;
        imgEl.appendChild(img);
      }
    }

    const shapeEl = qs('.pack-selected-board__shape');
    if (shapeEl) shapeEl.textContent = board.shape || '';

    const nameEl = qs('.pack-selected-board__name');
    if (nameEl) nameEl.textContent = board.title;

    const specsEl = qs('.pack-selected-board__specs');
    if (specsEl) {
      const parts = [];
      if (board.volume) {
        var volClean = String(board.volume).replace(/l/gi, '').replace(',', '.').trim();
        if (volClean) parts.push(volClean + ' L');
      }
      if (board.construction) parts.push(board.construction);
      specsEl.textContent = parts.join(' . ');
    }

    const boardPriceEl = qs('.pack-selected-board__price');
    if (boardPriceEl) boardPriceEl.textContent = board.price_formatted || formatMoney(board.price_cents);

    // Update category descriptions with new board name
    CATEGORIES.forEach(function (cat) {
      if (cat === 'wax' || cat === 'pad') return;
      const descEl = qs('[data-category-desc="' + cat + '"]');
      if (!descEl) return;
      const tpl = DATA.i18n['cat_' + cat + '_desc'] || '';
      descEl.textContent = tpl.replace('__BOARD__', board.title);
    });

    // Re-render each category grid
    CATEGORIES.forEach(function (cat) {
      const grid = qs('[data-grid="' + cat + '"]');
      if (!grid) return;
      const items = board[MF_KEYS[cat]] || [];
      const badge = (DATA.badges && DATA.badges[cat]) || '';

      qsa('.accessory-card:not(.accessory-card--skip)', grid).forEach(function (el) { el.remove(); });

      const skipCard = qs('.accessory-card--skip', grid);
      items.forEach(function (item, idx) {
        const card = buildCard(item, cat, idx === 0 ? badge : '', idx === 0);
        if (!card) return;
        if (skipCard) grid.insertBefore(card, skipCard);
        else grid.appendChild(card);
      });

      if (skipCard) {
        skipCard.setAttribute('aria-pressed', 'false');
        skipCard.classList.remove('selected');
      }
    });

    mountBoardVariantPicker(board);

    CATEGORIES.forEach(function (cat) { readInitialSelection(cat); });
    updateSidebar();
    return true;
  }

  // ------------------------------------------------------------------
  // Selection state
  // ------------------------------------------------------------------
  function readInitialSelection(category) {
    const selected = qs('[data-grid="' + category + '"] .accessory-card.selected');
    if (!selected) {
      state.selection[category] = null;
      return;
    }
    const variantId = selected.dataset.variantId;
    if (!variantId) {
      state.selection[category] = null;
      return;
    }
    state.selection[category] = {
      variantId: variantId,
      priceCents: parseInt(selected.dataset.price, 10) || 0,
      name: selected.dataset.name || '',
      handle: selected.dataset.handle || '',
      isPremium: selected.dataset.isPremium === 'true'
    };
  }

  function selectCard(card) {
    const category = card.dataset.category;
    if (!category) return;
    const wasSelected = card.classList.contains('selected');
    const grid = qs('[data-grid="' + category + '"]');
    if (!grid) return;

    qsa('.accessory-card', grid).forEach(function (c) {
      c.classList.remove('selected');
      c.setAttribute('aria-pressed', 'false');
    });

    if (wasSelected && !card.classList.contains('accessory-card--skip')) {
      const skipCard = qs('.accessory-card--skip', grid);
      if (skipCard) {
        skipCard.classList.add('selected');
        skipCard.setAttribute('aria-pressed', 'true');
      }
      state.selection[category] = null;
    } else {
      card.classList.add('selected');
      card.setAttribute('aria-pressed', 'true');
      const variantId = card.dataset.variantId;
      if (!variantId || card.classList.contains('accessory-card--skip')) {
        state.selection[category] = null;
      } else {
        state.selection[category] = {
          variantId: variantId,
          priceCents: parseInt(card.dataset.price, 10) || 0,
          name: card.dataset.name || '',
          handle: card.dataset.handle || '',
          isPremium: card.dataset.isPremium === 'true'
        };
      }
    }

    updateSidebar();
  }

  // ------------------------------------------------------------------
  // Sidebar + pricing update
  // ------------------------------------------------------------------
  function updateSidebar() {
    const sidebar = qs('#packSidebar');
    if (!sidebar) return;

    CATEGORIES.forEach(function (cat) {
      const nameEl = document.getElementById('pack-selection-name-' + cat);
      const priceEl = document.getElementById('pack-selection-price-' + cat);
      if (!nameEl || !priceEl) return;
      const sel = state.selection[cat];
      if (sel) {
        nameEl.textContent = sel.name;
        nameEl.classList.remove('pack-selection-item__name--empty');
        priceEl.textContent = formatMoney(sel.priceCents);
        priceEl.classList.remove('pack-selection-item__price--empty');
      } else {
        nameEl.textContent = DATA.i18n.not_selected || 'Non selectionne';
        nameEl.classList.add('pack-selection-item__name--empty');
        priceEl.textContent = '-';
        priceEl.classList.add('pack-selection-item__price--empty');
      }
    });

    const boardPrice = parseInt(sidebar.dataset.boardPrice, 10) || 0;
    const premiumHousseDiscountCents = parseInt(sidebar.dataset.premiumHousseDiscount, 10) || 0;
    const basecoat = DATA.basecoat_wax || null;
    const basecoatPriceCents = basecoat ? (parseInt(basecoat.price_cents, 10) || 0) : 0;

    const accessoryCents = CATEGORIES.reduce(function (sum, cat) {
      const s = state.selection[cat];
      return sum + (s ? s.priceCents : 0);
    }, 0);
    const selectedCount = CATEGORIES.filter(function (cat) { return state.selection[cat]; }).length;
    const board = DATA.boards[state.boardHandle];
    const boardAvailable = !!(board && (board.variants || []).some(function (v) {
      return v.available && String(v.id) === String(sidebar.dataset.boardVariantId);
    }));

    // Basecoat is always included in the pack -- added to subtotal, then discounted 100% back out.
    const subtotal = boardPrice + accessoryCents + basecoatPriceCents;

    // Premium housse discount -- applies only when the selected housse is flagged is_premium.
    const housseSel = state.selection.housse;
    const premiumHousseApplies = !!(housseSel && housseSel.isPremium && premiumHousseDiscountCents > 0);
    const premiumHousseDiscount = premiumHousseApplies ? premiumHousseDiscountCents : 0;

    // Basecoat is 100% off -- shown as separate discount line.
    const basecoatDiscount = basecoat ? basecoatPriceCents : 0;

    // Ailerons are 100% off via the FINS_PACK discount code (same rule as PDP Free Fins Offer).
    const aileronsSel = state.selection.ailerons;
    const aileronsDiscount = aileronsSel ? aileronsSel.priceCents : 0;

    const total = subtotal - premiumHousseDiscount - basecoatDiscount - aileronsDiscount;

    const subEl = document.getElementById('packSubtotal');
    const totEl = document.getElementById('packTotal');

    if (subEl) subEl.textContent = formatMoney(subtotal);
    if (totEl) totEl.textContent = formatMoney(total);

    // Premium housse discount row
    const discPremiumEl = document.getElementById('packDiscountPremiumHousse');
    const discPremiumRow = document.getElementById('packDiscountRowPremiumHousse');
    if (premiumHousseApplies && discPremiumEl && discPremiumRow) {
      discPremiumEl.textContent = '-' + formatMoney(premiumHousseDiscount);
      discPremiumRow.hidden = false;
    } else if (discPremiumRow) {
      discPremiumRow.hidden = true;
    }

    // Basecoat free row (always visible when basecoat is configured)
    const discBasecoatEl = document.getElementById('packDiscountBasecoat');
    const discBasecoatRow = document.getElementById('packDiscountRowBasecoat');
    if (basecoat && discBasecoatEl && discBasecoatRow) {
      discBasecoatEl.textContent = '-' + formatMoney(basecoatDiscount);
      discBasecoatRow.hidden = false;
    } else if (discBasecoatRow) {
      discBasecoatRow.hidden = true;
    }

    // Ailerons free row -- shown whenever a fin product (not skip) is selected.
    const discAileronsEl = document.getElementById('packDiscountAilerons');
    const discAileronsRow = document.getElementById('packDiscountRowAilerons');
    if (aileronsSel && discAileronsEl && discAileronsRow) {
      discAileronsEl.textContent = '-' + formatMoney(aileronsDiscount);
      discAileronsRow.hidden = false;
    } else if (discAileronsRow) {
      discAileronsRow.hidden = true;
    }

    // Basecoat line in the selection summary (always shown when basecoat is configured)
    const basecoatLineEl = document.getElementById('pack-selection-basecoat');
    const basecoatNameEl = document.getElementById('pack-selection-name-basecoat');
    if (basecoat && basecoatLineEl && basecoatNameEl) {
      basecoatNameEl.textContent = basecoat.title;
      basecoatLineEl.hidden = false;
    } else if (basecoatLineEl) {
      basecoatLineEl.hidden = true;
    }

    const cta = document.getElementById('packAddToCart');
    const ctaLabel = document.getElementById('packCtaLabel');
    if (cta && ctaLabel) {
      if (selectedCount === 0 || !boardAvailable) {
        cta.disabled = true;
        ctaLabel.textContent = cta.dataset.labelSelect || 'Select accessories';
      } else {
        cta.disabled = false;
        ctaLabel.textContent = cta.dataset.labelAdd || 'Add pack to cart';
      }
    }

    // Mirror state on mobile mini-CTA in toggle bar
    const miniCta = document.getElementById('packToggleCta');
    const miniCtaLabel = document.getElementById('packToggleCtaLabel');
    if (miniCta && miniCtaLabel) {
      if (selectedCount === 0 || !boardAvailable) {
        miniCta.disabled = true;
        miniCtaLabel.textContent = miniCta.dataset.labelSelect || 'Select';
      } else {
        miniCta.disabled = false;
        miniCtaLabel.textContent = miniCta.dataset.labelAdd || 'Add';
      }
    }

    const toggleCount = document.getElementById('packToggleCount');
    const togglePrice = document.getElementById('packTogglePrice');
    if (toggleCount) {
      let countText;
      if (selectedCount === 0) {
        countText = DATA.i18n.item_count_zero || '';
      } else if (selectedCount === 1) {
        countText = DATA.i18n.item_count_one || '';
      } else {
        countText = (DATA.i18n.item_count_other || '').replace('{{ count }}', String(selectedCount + 1));
      }
      toggleCount.textContent = countText;
    }
    if (togglePrice) togglePrice.textContent = formatMoney(total);
  }

  // ------------------------------------------------------------------
  // Cart submit
  // ------------------------------------------------------------------
  function addPackToCart() {
    const cta = document.getElementById('packAddToCart');
    const ctaLabel = document.getElementById('packCtaLabel');
    if (!cta || cta.disabled) return;

    const sidebar = qs('#packSidebar');
    const boardVariantId = sidebar ? sidebar.dataset.boardVariantId : '';
    const board = DATA.boards[state.boardHandle];
    const boardVariant = board && (board.variants || []).find(function (v) {
      return String(v.id) === String(boardVariantId);
    });
    if (!boardVariant || !boardVariant.available) {
      console.error('[pack-builder] refusing unavailable board variant');
      if (ctaLabel) ctaLabel.textContent = cta.dataset.labelError || 'Unavailable';
      return;
    }

    const packId = 'pack_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
    const items = [];
    if (boardVariantId) {
      items.push({
        id: parseInt(boardVariantId, 10),
        quantity: 1,
        properties: { '_pack_id': packId, '_pack_role': 'board' }
      });
    }

    let invalidSelection = false;
    CATEGORIES.forEach(function (cat) {
      const sel = state.selection[cat];
      const item = (board && board[MF_KEYS[cat]] || []).find(function (candidate) {
        if (candidate.handle !== (sel && sel.handle) || candidate.available !== true) return false;
        if (cat === 'pad') return (candidate.variants || []).some(function (v) {
          return v.available && String(v.id) === String(sel.variantId);
        });
        return String(candidate.variant_id) === String(sel && sel.variantId);
      });
      if (sel && !item) invalidSelection = true;
      if (sel && sel.variantId && item) {
        const props = { '_pack_id': packId, '_pack_role': cat };
        if (cat === 'housse' && sel.isPremium) props['_pack_housse_premium'] = 'true';
        items.push({ id: parseInt(sel.variantId, 10), quantity: 1, properties: props });
      }
    });

    // Basecoat wax -- always included in the pack, discounted 100% server-side.
    if (invalidSelection) {
      console.error('[pack-builder] refusing unavailable accessory selection');
      return;
    }
    const basecoat = DATA.basecoat_wax;
    if (basecoat && basecoat.variant_id && basecoat.available === true) {
      items.push({
        id: parseInt(basecoat.variant_id, 10),
        quantity: 1,
        properties: { '_pack_id': packId, '_pack_role': 'basecoat' }
      });
    }

    if (items.length === 0) return;

    cta.disabled = true;
    if (ctaLabel) ctaLabel.textContent = cta.dataset.labelAdding || 'Adding...';

    const miniCta = document.getElementById('packToggleCta');
    const miniCtaLabel = document.getElementById('packToggleCtaLabel');
    if (miniCta) miniCta.disabled = true;
    if (miniCtaLabel && miniCta) miniCtaLabel.textContent = miniCta.dataset.labelAdding || 'Adding...';

    // Add items then chain discount codes to /cart via full-page navigation.
    // Drawer flow requires automatic discounts (not code-based). Code-based
    // BXGY/Basic discounts require the /discount/CODE redirect route, which
    // cannot be driven reliably from fetch(). TODO: migrate BASECOAT_PACK +
    // FINS_PACK to automatic discounts keyed on _pack_id line property, then
    // restore a drawer-first flow.
    const rootPath = routesRoot();
    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function () {
      const finsCode = DATA.fins_discount_code || '';
      const cartUrl = rootPath + 'cart';

      // Shopify's discount_code cookie stores exactly ONE code at a time.
      // Neither comma-syntax (/discount/A,B) nor nested redirect chains
      // (/discount/A?redirect=/discount/B) work: comma is stored literally
      // (no match), nested redirects overwrite the previous cookie.
      // Single-code strategy: FINS_PACK BXGY covers ailerons + basecoat
      // (sex-wax-blue-tropic) in a single customerGets pool with quantity=2.
      // Basecoat is always added with ailerons when user selects them.
      let target = cartUrl;
      if (finsCode) {
        target = rootPath + 'discount/' + encodeURIComponent(finsCode) +
                 '?redirect=' + encodeURIComponent(cartUrl);
      }
      window.location.href = target;
    }).catch(function (err) {
      console.error('[pack-builder] add to cart failed', err);
      cta.disabled = false;
      if (ctaLabel) ctaLabel.textContent = cta.dataset.labelError || 'Error, please retry';
      if (miniCta) miniCta.disabled = false;
      if (miniCtaLabel && miniCta) miniCtaLabel.textContent = miniCta.dataset.labelError || 'Error';
    });
  }

  // ------------------------------------------------------------------
  // Event bindings
  // ------------------------------------------------------------------
  function bindEvents() {
    // Board length / size variant chips (sidebar)
    root.addEventListener('click', function (e) {
      const chip = e.target.closest('.board-variant-chip');
      if (!chip || !root.contains(chip)) return;
      if (chip.disabled) return;
      const handle = state.boardHandle;
      const board = DATA.boards[handle];
      if (!board) return;
      e.preventDefault();
      const id = chip.dataset.variantId;
      const v = (board.variants || []).find(function (x) {
        return String(x.id) === String(id);
      });
      if (!v || !v.available) return;

      const sb = qs('#packSidebar');
      if (sb) {
        sb.dataset.boardPrice = String(v.price_cents);
        sb.dataset.boardVariantId = String(v.id);
      }

      const boardPriceEl = qs('.pack-selected-board__price');
      if (boardPriceEl) {
        boardPriceEl.textContent = v.price_formatted || formatMoney(v.price_cents);
      }

      const wrap = qs('.pack-selected-board__image');
      if (wrap && v.image_url) {
        let img = qs('img', wrap);
        if (!img) {
          img = document.createElement('img');
          img.loading = 'lazy';
          clearChildren(wrap);
          wrap.appendChild(img);
        }
        img.src = v.image_url;
        img.alt = (board.title || '') + ' \u2014 ' + (v.label || v.title || '');
      }

      qsa('.board-variant-chip', root).forEach(function (c) {
        const on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      updateSidebar();
    });

    function selectPadSwatch(sw, e) {
      if (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      if (!sw || sw.dataset.available === 'false') return;
      const card = sw.closest('.accessory-card');
      if (!card) return;
      qsa('.pad-variant-swatch', card).forEach(function (s) {
        s.classList.remove('is-active');
        s.setAttribute('aria-pressed', 'false');
      });
      sw.classList.add('is-active');
      sw.setAttribute('aria-pressed', 'true');
      card.dataset.variantId = sw.dataset.variantId;
      card.dataset.price = sw.dataset.price;
      const padBase = card.dataset.padBaseName || card.dataset.name || 'Pad';
      card.dataset.padBaseName = padBase;
      card.dataset.name = padBase + ' -- ' + (sw.dataset.title || '');
      const priceLabel = card.querySelector('.accessory-card__price');
      if (priceLabel) priceLabel.textContent = formatMoney(parseInt(sw.dataset.price, 10) || 0);
      const imgEl = card.querySelector('.accessory-card__image img');
      const newImg = sw.dataset.image;
      if (imgEl && newImg) {
        imgEl.src = newImg;
        imgEl.removeAttribute('srcset');
        imgEl.alt = padBase + ' -- ' + (sw.dataset.title || '');
      }
      if (card.classList.contains('selected')) {
        state.selection.pad = {
          variantId: sw.dataset.variantId,
          priceCents: parseInt(sw.dataset.price, 10) || 0,
          name: card.dataset.name,
          handle: card.dataset.handle || '',
          isPremium: false
        };
        updateSidebar();
      }
    }

    // Pad variant swatches -- intercept before card toggle and support keyboard.
    root.addEventListener('click', function (e) {
      const sw = e.target.closest('.pad-variant-swatch');
      if (!sw) return;
      selectPadSwatch(sw, e);
    }, true);
    root.addEventListener('keydown', function (e) {
      const sw = e.target.closest('.pad-variant-swatch');
      if (!sw || (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar')) return;
      selectPadSwatch(sw, e);
    }, true);

    root.addEventListener('click', function (e) {
      const card = e.target.closest('.accessory-card');
      if (card && root.contains(card)) {
        e.preventDefault();
        selectCard(card);
      }
    });

    const cta = document.getElementById('packAddToCart');
    if (cta) cta.addEventListener('click', addPackToCart);

    // Mobile mini-CTA in toggle bar -- 1-tap add-to-cart without expanding drawer
    const miniCta = document.getElementById('packToggleCta');
    if (miniCta) {
      miniCta.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        addPackToCart();
      });
    }

    const toggle = document.getElementById('packSidebarToggle');
    if (toggle) {
      toggle.addEventListener('click', function () {
        const sb = document.getElementById('packSidebar');
        if (!sb) return;
        const isCollapsed = sb.classList.toggle('collapsed');
        toggle.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
      });
    }
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    const urlBoard = getUrlBoard();
    const step0 = document.getElementById('packStep0');
    const step1 = document.getElementById('packStep1');

    if (urlBoard && DATA.boards[urlBoard]) {
      if (step0) step0.hidden = true;
      if (step1) step1.hidden = false;
      swapToBoard(urlBoard);
    } else if (urlBoard && !DATA.boards[urlBoard]) {
      // Unknown ?board= handle -- fall back to Step 0 picker rather than a
      // confusing empty Step 1 with the SSR default.
      console.warn('[pack-builder] unknown board handle in URL, showing picker:', urlBoard);
      if (step1) step1.hidden = true;
      if (step0) step0.hidden = false;
    } else if (state.boardHandle && DATA.boards[state.boardHandle]) {
      if (step0) step0.hidden = true;
      if (step1) step1.hidden = false;
      swapToBoard(state.boardHandle);
    } else {
      if (step1) step1.hidden = true;
      if (step0) step0.hidden = false;
    }

    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
